import React, {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Maps each Section component name → its DOM id.
// Enables deep-linking via /present#hero even when only 3 slides are mounted.
const SLIDE_ID_BY_TYPE = {
  SectionHero: 'hero',
  SectionAgenda: 'agenda',
  SectionEcosystem: 'ecosystem',
  SectionBroschure: 'broschure',
  SectionDisclaimer: 'disclaimer',
  SectionMoeglichkeiten: 'moeglichkeiten',
  SectionChance: 'chance',
  SectionWertentwicklung: 'wertentwicklung',
  SectionHauptgruende: 'hauptgruende',
  SectionMarkt: 'markt',
  SectionInnovation: 'innovation',
  SectionGlobalNetwork: 'global-network',
  SectionSicherheit: 'sicherheit',
  SectionBoxVideo: 'box-video',
  SectionZertifiziert: 'zertifiziert',
  SectionZertifiziertVideo: 'zertifiziert-video',
  SectionKimberley: 'kimberley',
  SectionPlaene: 'plaene',
  SectionVIP: 'vip',
  SectionPlaeneVIP: 'plaene-vip',
  SectionErsparnisse: 'ersparnisse',
  SectionMoeglichkeitenNach: 'moeglichkeiten-nach',
  SectionTraditionellerWeg: 'trad-weg',
  SectionWieMoeglich: 'wie-moeglich',
  SectionStigWeg: 'stig-weg',
  SectionSchliffVlog: 'schliff-vlog',
  SectionDualTimeline: 'dual-timeline',
  SectionExplainer: 'explainer',
  SectionMarioIntro: 'mario-intro',
  SectionThomasIntro: 'thomas-intro',
  SectionEinfacherWeg: 'einfacher-weg',
  SectionPause: 'pause',
  SectionBusiness: 'business',
  SectionUnilevelBonus: 'unilevel-bonus',
  SectionInfinityBonus: 'infinity-bonus',
  SectionMatchingBonus: 'matching-bonus',
  SectionWorldPools: 'world-pools',
  SectionRangBonus: 'rang-bonus',
  SectionKarriere: 'karriere',
  SectionCTA: 'cta',
  SectionInstagram: 'instagram',
};

function resolveSlideId(childElement) {
  if (!childElement || !childElement.type) return null;
  const name = childElement.type.name || childElement.type.displayName;
  // Unwrap the <When visible={...}> guard so we can inspect the real Section.
  if (name === 'When') {
    const inner = Children.toArray(childElement.props?.children).filter(Boolean)[0];
    return resolveSlideId(inner);
  }
  return SLIDE_ID_BY_TYPE[name] || null;
}

/**
 * SlideDeck — Keynote-style presentation container.
 *
 * Renders each child as a full-viewport slide (100vw × 100vh) with
 * fade transitions between them. Only one slide is visible / interactive
 * at a time. Navigation:
 *   • Keyboard: ← / → / ↑ / ↓ / Space / PageUp / PageDown / Home / End
 *   • Touch:    swipe left / right
 *   • Mouse:    click far-left edge = prev, far-right edge = next
 *
 * Passing `initialSlideId="hero"` will jump to the slide whose top-level
 * child element has `id="hero"`.
 */
export default function SlideDeck({ children, onIndexChange }) {
  // Wichtig: Children mit `visible === false` (z. B. <When visible={false}>…)
  // müssen aus der Slide-Liste entfernt werden, sonst zählt der Slide-Counter
  // ausgeblendete Folien fälschlicherweise mit („09 / 40" statt „09 / 33").
  const slides = Children.toArray(children).filter((child) => {
    if (!child) return false;
    if (child.props && child.props.visible === false) return false;
    return true;
  });
  const [index, setIndex] = useState(0);
  const total = slides.length;
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const wheelBusyRef = useRef(false);

  // Build id → index map once. Works even when only 3 slides are mounted
  // because we inspect React element types, not the DOM.
  const idToIndex = useMemo(() => {
    const m = {};
    slides.forEach((slide, i) => {
      const id = resolveSlideId(slide);
      if (id) m[id] = i;
    });
    return m;
  }, [slides.length]);

  /* -----------------------------------------------------------------
     Deep-link on first mount AND when the hash changes at runtime.
     ----------------------------------------------------------------- */
  useEffect(() => {
    const jumpToHash = () => {
      const hash = decodeURIComponent(window.location.hash || '').replace(/^#/, '');
      if (!hash) return;
      const target = idToIndex[hash];
      if (typeof target === 'number') setIndex(target);
    };
    jumpToHash();
    window.addEventListener('hashchange', jumpToHash);
    return () => window.removeEventListener('hashchange', jumpToHash);
  }, [idToIndex]);

  const goto = useCallback(
    (i) => {
      const clamped = Math.max(0, Math.min(total - 1, i));
      // Sofort-Stopp: JEDES laufende, nicht-persistente Medium wird beim
      // Folienwechsel synchron pausiert (Absicherung gegen weiterlaufenden
      // Ton, unabhängig vom 80ms-Cleanup-Timer unten).
      if (clamped !== index) {
        document.querySelectorAll('video, audio').forEach((el) => {
          try {
            if (el.dataset && (el.dataset.slidePersist === 'true' || el.dataset.testid === 'audio-loop' || el.dataset.testid === 'global-network-video' || el.dataset.testid === 'cta-video')) return;
            if (clamped !== 0 || index !== 0) {
              if (!el.paused && !(clamped === 0 && el.closest('#hero'))) el.pause();
            }
          } catch (err) { /* noop */ }
        });
      }
      setIndex(clamped);
      if (typeof onIndexChange === 'function') onIndexChange(clamped);
    },
    [total, onIndexChange, index],
  );

  const next = useCallback(() => goto(index + 1), [index, goto]);
  const prev = useCallback(() => goto(index - 1), [index, goto]);

  // Fragment-Unterstützung auch für Pfeiltasten/Klicker: liefert true,
  // wenn auf der aktiven Folie noch ein Fragment aufgedeckt wurde.
  const consumeFragment = useCallback(() => {
    const frame = document.querySelector(`.slide-frame[data-slide-i="${index}"]`);
    const fragEl = frame && frame.querySelector('[data-fragments-remaining]');
    if (fragEl && parseInt(fragEl.dataset.fragmentsRemaining || '0', 10) > 0) {
      fragEl.dispatchEvent(new CustomEvent('fragment-advance'));
      return true;
    }
    return false;
  }, [index]);

  // Pause- & CTA-Folie: JEDER Vorwärts-Weg (Klick, Taste, Klicker) startet
  // zuerst die Musik; erst der nächste Vorwärts-Schritt blättert weiter.
  // Intro-Videos: erster Vorwärts-Impuls entstummt statt zu blättern.
  const startPauseMediaIfNeeded = useCallback(() => {
    const frame = document.querySelector(`.slide-frame[data-slide-i="${index}"]`);
    if (!frame) return false;
    // Intro-Videos — 1. Vorwärts-Impuls unmute-t den Ton (kein Advance).
    if (
      frame.querySelector('#thomas-intro') ||
      frame.querySelector('#mario-intro') ||
      frame.querySelector('#explainer')
    ) {
      const intro = frame.querySelector('video');
      if (intro && intro.muted) {
        try {
          intro.muted = false;
          intro.removeAttribute('muted');
          intro.volume = 1.0;
          if (intro.paused) {
            const pl = intro.play();
            if (pl && typeof pl.then === 'function') pl.catch(() => {});
          }
        } catch (_) { /* noop */ }
        return true;
      }
      return false; // schon entstummt → normal weiterblättern
    }
    // Pause-Folie — Song-Video (mit Audio) beim ersten Vorwärts-Impuls starten
    const pauseVid = frame.querySelector('[data-testid="pause-video"]');
    if (pauseVid && frame.querySelector('#pause') && pauseVid.paused) {
      pauseVid.play().catch(() => {});
      return true;
    }
    // Endfolie (CTA) — Outro-Musik erst per erstem Klick starten
    const outroEl = document.querySelector('[data-testid="outro-audio-persistent"]');
    if (outroEl && frame.querySelector('#cta') && outroEl.paused) {
      outroEl.play().catch(() => {});
      return true;
    }
    return false;
  }, [index]);

  /* -----------------------------------------------------------------
     Keyboard navigation
     ----------------------------------------------------------------- */
  useEffect(() => {
    const onKey = (e) => {
      // Ignore keystrokes while user is typing into a form field.
      const tag = e.target && e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          if (startPauseMediaIfNeeded()) break;
          if (!consumeFragment()) next();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          prev();
          break;
        case 'Home':
          e.preventDefault();
          goto(0);
          break;
        case 'End':
          e.preventDefault();
          goto(total - 1);
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, goto, total, consumeFragment, startPauseMediaIfNeeded]);

  /* -----------------------------------------------------------------
     Mouse wheel / trackpad — advance one slide per fluent motion.
     Debounced so a single flick doesn't blow through 5 slides.
     ----------------------------------------------------------------- */
  useEffect(() => {
    let cooldown = null;
    const onWheel = (e) => {
      if (wheelBusyRef.current) return;
      const dominant = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(dominant) < 24) return;
      wheelBusyRef.current = true;
      if (dominant > 0) {
        if (!startPauseMediaIfNeeded()) next();
      } else prev();
      clearTimeout(cooldown);
      cooldown = setTimeout(() => {
        wheelBusyRef.current = false;
      }, 650);
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      window.removeEventListener('wheel', onWheel);
      clearTimeout(cooldown);
    };
  }, [next, prev, startPauseMediaIfNeeded]);

  /* -----------------------------------------------------------------
     Touch — horizontal or vertical swipe (whichever is dominant).
     ----------------------------------------------------------------- */
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const absX = Math.abs(dx);
    const absY = Math.abs(dy);
    // Require a decent swipe distance to avoid firing on taps.
    if (Math.max(absX, absY) > 55) {
      const dominant = absX > absY ? dx : dy;
      if (dominant < 0) {
        if (!startPauseMediaIfNeeded()) next();
      } else prev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  /* -----------------------------------------------------------------
     Mouse click navigation:
       • Left click  → next  (default click on empty slide area)
       • Right click → prev  (context menu suppressed)
     Interactive elements (buttons, links, video/audio, inputs …) are
     ignored so they still behave normally.
     ----------------------------------------------------------------- */
  const isInteractiveTarget = (target) => {
    if (!target || !target.closest) return false;
    return !!target.closest(
      'button, a, input, textarea, select, label, video, audio, iframe, [role="button"], [data-slide-interactive]',
    );
  };

  const onDeckClick = (e) => {
    const target = e.target;
    // Hero (slide 0): the diamond animation should run continuously,
    // and any click — even directly on the video — advances to the next
    // slide. Only real interactive controls (Dashboard/ESC/Kino toggle)
    // are exempted.
    if (index === 0) {
      if (
        target && target.closest && target.closest(
          'header, [data-testid="exit-presentation-btn"], [data-testid="dashboard-exit-btn"], [data-testid="cinematic-toggle-btn"], [data-testid="dash-exit-btn"], button, a'
        )
      ) return;
      next();
      return;
    }

    // Fragment support: a slide may hide parts until an extra click
    // (e.g. Dual-Timeline reveals its STIG lane on a separate click).
    const frameForFrag = document.querySelector(
      `.slide-frame[data-slide-i="${index}"]`,
    );
    const fragEl = frameForFrag && frameForFrag.querySelector('[data-fragments-remaining]');
    if (fragEl && parseInt(fragEl.dataset.fragmentsRemaining || '0', 10) > 0) {
      if (isInteractiveTarget(target)) return;
      fragEl.dispatchEvent(new CustomEvent('fragment-advance'));
      return;
    }

    // Look for the primary video/audio living on the currently-active
    // slide. This lets us give video-heavy slides a two-step click model:
    // 1st click plays the media, subsequent clicks (once it is running)
    // advance to the next slide. Right-click pauses it.
    const activeFrame = document.querySelector(
      `.slide-frame[data-slide-i="${index}"]`,
    );

    // Global-Network (Internationale Struktur): das Globus-Video läuft
    // dauerhaft (autoplay, muted, playsInline) — hier gibt es KEIN
    // 2-Klick-Modell, jeder Klick blättert direkt weiter (wie auf Hero).
    if (activeFrame && activeFrame.querySelector('#global-network')) {
      if (
        target && target.closest && target.closest(
          'header, [data-testid="exit-presentation-btn"], [data-testid="dashboard-exit-btn"], [data-testid="cinematic-toggle-btn"], [data-testid="dash-exit-btn"], button, a'
        )
      ) return;
      next();
      return;
    }

    // Intro-Videos (Thomas, Mario, Explainer) — werden vom
    // useAutoplayIntroVideo Hook nur für Autoplay/Cleanup verwaltet.
    // Der Klick-Handler LEBT HIER — nur so ist der user-gesture-scope
    // eindeutig und Chrome/Safari respektieren den Unmute-Aufruf:
    //   1. Klick auf stummes Video  → muted=false (Ton kommt SOFORT)
    //   2. Klick auf Video mit Ton  → advance zur nächsten Folie
    if (activeFrame && (
      activeFrame.querySelector('#thomas-intro') ||
      activeFrame.querySelector('#mario-intro') ||
      activeFrame.querySelector('#explainer')
    )) {
      if (
        target && target.closest && target.closest(
          'header, [data-testid="exit-presentation-btn"], [data-testid="dashboard-exit-btn"], [data-testid="cinematic-toggle-btn"], [data-testid="dash-exit-btn"], button, a'
        )
      ) return;
      const introVideo = activeFrame.querySelector('video');
      if (introVideo && introVideo.muted) {
        // 1. Klick — Ton einschalten (User-Geste, Browser erlaubt Unmute).
        try {
          introVideo.muted = false;
          introVideo.removeAttribute('muted');
          introVideo.volume = 1.0;
          if (introVideo.paused) {
            const pl = introVideo.play();
            if (pl && typeof pl.then === 'function') pl.catch(() => {});
          }
        } catch (_) { /* noop */ }
        e.stopPropagation();
        return;
      }
      // Video ist bereits entstummt → 2. Klick blättert weiter.
      next();
      return;
    }

    // Pause-Folie: 2-Klick-Modell für das Song-Video (mit eingebettetem
    // Audio). Klick 1 startet das Video (Musik + Countdown), Klick 2 geht
    // weiter.
    const pauseVideoEl = activeFrame && activeFrame.querySelector('[data-testid="pause-video"]');
    if (pauseVideoEl && activeFrame && activeFrame.querySelector('#pause')) {
      if (pauseVideoEl.paused) {
        pauseVideoEl.play().catch(() => {});
        return;
      }
      if (isInteractiveTarget(target)) return;
      next();
      return;
    }

    // Endfolie (CTA): 2-Klick-Modell für die Outro-Musik. Klick 1 startet
    // die Musik, Klick 2 geht weiter. Das CTA-Video läuft parallel als
    // stummes Ambient-Loop (data-slide-persist / exempt).
    const outroAudioEl = document.querySelector('[data-testid="outro-audio-persistent"]');
    if (outroAudioEl && activeFrame && activeFrame.querySelector('#cta')) {
      if (outroAudioEl.paused) {
        outroAudioEl.play().catch(() => {});
        return;
      }
      if (isInteractiveTarget(target)) return;
      next();
      return;
    }

    const primaryMedia = activeFrame && activeFrame.querySelector('video, audio');

    if (primaryMedia) {
      if (primaryMedia.paused) {
        // Media not running yet → start it, stay on the slide.
        primaryMedia.play().catch(() => {});
        e.stopPropagation();
        return;
      }
      // Media is running → advance to the next slide.
      if (
        target && target.closest && target.closest(
          'header, [data-testid="exit-presentation-btn"], [data-testid="dashboard-exit-btn"], [data-testid="cinematic-toggle-btn"], [data-testid="dash-exit-btn"], button, a'
        )
      ) return;
      next();
      return;
    }

    // Ordinary slides — no media: just advance on click.
    if (isInteractiveTarget(target)) return;
    next();
  };

  const onDeckContextMenu = (e) => {
    // Right-click: on a slide that has a currently-playing video/audio,
    // pause it instead of navigating backwards. Once it is paused the
    // next right-click behaves normally (go to previous slide).
    const activeFrame = document.querySelector(
      `.slide-frame[data-slide-i="${index}"]`,
    );
    // Pause-Folie: Rechtsklick pausiert das laufende Song-Video zuerst.
    const pauseVideoEl = activeFrame && activeFrame.querySelector('[data-testid="pause-video"]');
    if (pauseVideoEl && !pauseVideoEl.paused && activeFrame && activeFrame.querySelector('#pause')) {
      e.preventDefault();
      pauseVideoEl.pause();
      return;
    }
    // Endfolie: Rechtsklick pausiert die Outro-Musik.
    const outroAudioEl = document.querySelector('[data-testid="outro-audio-persistent"]');
    if (outroAudioEl && !outroAudioEl.paused && activeFrame && activeFrame.querySelector('#cta')) {
      e.preventDefault();
      outroAudioEl.pause();
      return;
    }
    const primaryMedia = activeFrame && activeFrame.querySelector('video, audio');
    if (primaryMedia && !primaryMedia.paused) {
      // Rechtsklick geht IMMER zurück (User-Wunsch) — laufende Medien
      // werden dabei gestoppt, nicht nur pausiert.
      try { primaryMedia.pause(); } catch (err) { /* noop */ }
    }
    // ACHTUNG: video/audio hier bewusst NICHT als "interaktiv" werten —
    // sonst kommt man von Video-Folien per Rechtsklick nie zurück.
    const interactive = e.target && e.target.closest && e.target.closest(
      'button, a, input, textarea, select, label, iframe, [role="button"], [data-slide-interactive]',
    );
    if (interactive) return;
    e.preventDefault();
    prev();
  };

  /* -----------------------------------------------------------------
     Whenever the active slide changes: strip autoplay from any video /
     audio inside the deck and pause it — EXCEPT on the Hero (slide 0),
     where the diamond animation is meant to run continuously.
     The presenter must intentionally click media on every other slide.
     ----------------------------------------------------------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      const activeFrame = document.querySelector(`.slide-frame[data-slide-i="${index}"]`);
      const media = document.querySelectorAll('.slide-deck video, .slide-deck audio');
      media.forEach((el) => {
        try {
          // Preserve the hero video: keep it playing and looped.
          if (index === 0 && activeFrame && activeFrame.contains(el)) {
            if (el.paused) el.play().catch(() => {});
            return;
          }
          // Persistent loop media (e.g. AudioLoop on the Pause slide,
          // background pause-video, Global-Network Globus-Video, CTA
          // Feuerwerk-Video) manages its own lifecycle via
          // IntersectionObserver. Do NOT force-pause it here — that
          // was silencing the pause-slide soundtrack and forcing the
          // globe/CTA video into a 2-click model on Safari.
          if (
            el.dataset && (
              el.dataset.testid === 'audio-loop' ||
              el.dataset.testid === 'pause-video' ||
              el.dataset.testid === 'global-network-video' ||
              el.dataset.testid === 'cta-video' ||
              el.dataset.testid === 'mario-intro-video' ||
              el.dataset.testid === 'thomas-intro-video' ||
              el.dataset.testid === 'explainer-video' ||
              el.dataset.slidePersist === 'true'
            )
          ) {
            return;
          }
          el.autoplay = false;
          el.removeAttribute('autoplay');
          if (!el.paused) el.pause();
          el.currentTime = 0;
        } catch (_) { /* ignore */ }
      });
    }, 80);
    return () => clearTimeout(timer);
  }, [index]);

  return (
    <div
      className="slide-deck"
      data-testid="slide-deck"
      data-slide-index={index}
      data-slide-total={total}
      onClick={onDeckClick}
      onContextMenu={onDeckContextMenu}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        background: 'transparent',
        zIndex: 1,
      }}
    >
      {slides.map((slide, i) => {
        const isActive = i === index;
        // Render only the currently active slide. Adjacent slides used to be
        // preloaded to enable a cross-fade, but that also armed hidden
        // IntersectionObservers (audio loops etc.) which caused music to
        // start playing "one slide too early". Rendering only the active
        // frame guarantees clean media boundaries.
        return (
          <div
            key={i}
            className={`slide-frame${isActive ? ' slide-frame--active' : ''}`}
            data-slide-i={i}
            style={{
              position: 'absolute',
              inset: 0,
              display: isActive ? 'block' : 'none',
              opacity: isActive ? 1 : 0,
              pointerEvents: isActive ? 'auto' : 'none',
              transition: 'opacity 320ms ease-in-out',
              willChange: isActive ? 'opacity' : 'auto',
            }}
            aria-hidden={!isActive}
          >
            {isActive ? slide : null}
          </div>
        );
      })}

      {/* Deck-Fortschritt — hauchdünne Goldlinie oben */}
      <div
        className="deck-progress"
        aria-hidden
        style={{ width: `${((index + 1) / Math.max(1, total)) * 100}%` }}
      />

      {/* Slide counter — bottom-right */}
      <div
        className="slide-counter"
        data-testid="slide-counter"
        style={{
          position: 'fixed',
          bottom: 18,
          right: 26,
          color: 'rgba(255,255,255,0.55)',
          fontSize: 12,
          letterSpacing: '0.34em',
          fontFamily: 'Manrope, sans-serif',
          fontWeight: 300,
          pointerEvents: 'none',
          zIndex: 60,
          userSelect: 'none',
        }}
      >
        {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </div>

      {/* Left click zone — advance (kept as extra hit-target for peripheral clicks) */}
      <div
        onClick={next}
        data-testid="slide-next-zone"
        aria-label="Next slide"
        role="button"
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          width: '4vw',
          maxWidth: 80,
          zIndex: 40,
          cursor: index < total - 1 ? 'pointer' : 'default',
        }}
      />
    </div>
  );
}
